import { useState } from 'react';
import { Send } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ERROR_EVENT_TEST_TEMPLATES } from './errorEventTestTemplates';
import { CreateTestErrorEventRequest } from '../../../services/api';

const EMPTY_FORM: CreateTestErrorEventRequest = {
  service: '',
  errorIdentifier: '',
  customerId: '',
  agentId: '',
  sessionId: '',
  errorSource: '',
  error: '',
  info: '',
  help: '',
};

export interface ErrorEventTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateTestErrorEventRequest) => Promise<void>;
  submitting: boolean;
}

/** Dialog zum Auslösen eines synthetischen Test-Events — Vorlagen sind rein frontend-seitig, alles bleibt editierbar. */
export function ErrorEventTestDialog({ open, onOpenChange, onSubmit, submitting }: ErrorEventTestDialogProps) {
  const [form, setForm] = useState<CreateTestErrorEventRequest>(EMPTY_FORM);
  const [templateId, setTemplateId] = useState<string>('');

  const handleTemplateSelect = (id: string) => {
    setTemplateId(id);
    const template = ERROR_EVENT_TEST_TEMPLATES.find((t) => t.id === id);
    if (template) {
      setForm(template.values);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setForm(EMPTY_FORM);
      setTemplateId('');
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    const payload: CreateTestErrorEventRequest = {
      service: form.service.trim(),
      errorIdentifier: form.errorIdentifier.trim(),
      customerId: form.customerId?.trim() || undefined,
      agentId: form.agentId?.trim() || undefined,
      sessionId: form.sessionId?.trim() || undefined,
      errorSource: form.errorSource?.trim() || undefined,
      error: form.error,
      info: form.info,
      help: form.help,
    };
    await onSubmit(payload);
    handleOpenChange(false);
  };

  const isValid =
    form.service.trim().length > 0 &&
    form.errorIdentifier.trim().length > 0 &&
    form.error.trim().length > 0 &&
    form.info.trim().length > 0 &&
    form.help.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Test-Event erzeugen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="test-template">Vorlage</Label>
            <Select value={templateId} onValueChange={handleTemplateSelect}>
              <SelectTrigger id="test-template">
                <SelectValue placeholder="Vorlage wählen (optional)" />
              </SelectTrigger>
              <SelectContent>
                {ERROR_EVENT_TEST_TEMPLATES.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Vorlagen sind im Frontend hinterlegt — Auswahl befüllt die Felder, alles bleibt editierbar.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="test-service">Service</Label>
              <Input
                id="test-service"
                value={form.service}
                onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="test-error-identifier">Fehler-Identifier</Label>
              <Input
                id="test-error-identifier"
                placeholder="z.B. session_start_failed"
                value={form.errorIdentifier}
                onChange={(e) => setForm((f) => ({ ...f, errorIdentifier: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="test-customer-id">Kunden-ID (optional)</Label>
              <Input
                id="test-customer-id"
                value={form.customerId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="test-agent-id">Agent-ID (optional)</Label>
              <Input
                id="test-agent-id"
                value={form.agentId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, agentId: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="test-session-id">Session-ID (optional)</Label>
              <Input
                id="test-session-id"
                value={form.sessionId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, sessionId: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="test-error-source">Error Source (optional)</Label>
              <Input
                id="test-error-source"
                value={form.errorSource ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, errorSource: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="test-error">Error</Label>
            <Textarea
              id="test-error"
              rows={2}
              value={form.error}
              onChange={(e) => setForm((f) => ({ ...f, error: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="test-info">Info</Label>
            <Textarea
              id="test-info"
              rows={2}
              value={form.info}
              onChange={(e) => setForm((f) => ({ ...f, info: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="test-help">Help</Label>
            <Textarea
              id="test-help"
              rows={2}
              value={form.help}
              onChange={(e) => setForm((f) => ({ ...f, help: e.target.value }))}
            />
          </div>

          <p className="text-xs text-gray-500">timestamp wird beim Auslösen automatisch gesetzt.</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            <Send className="mr-2 h-4 w-4" />
            Test-Event auslösen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
